import { ApiResponse, SyncLog } from '../types';

/**
 * Kick off a registrar sync and wait for it by polling, not by holding the
 * request open.
 *
 * `POST /accounts/:id/sync` returns 202 immediately with a sync-log id; the work
 * continues server-side. A full Ilkari sync takes ~90s and Railway's edge proxy
 * severs connections held that long, handing the browser an HTML error page even
 * though the sync succeeded — polling sidesteps that entirely.
 */
export interface SyncOutcome {
  status: 'completed' | 'failed' | 'stalled' | 'timeout';
  domains_synced: number;
  domains_added: number;
  domains_updated: number;
  error?: string;
  /** True when this call attached to a sync that was already in flight. */
  alreadyRunning: boolean;
}

type FetchApi = <T>(path: string, options?: RequestInit) => Promise<T>;

const POLL_INTERVAL_MS = 2000;
/** Generous ceiling: sync time scales with portfolio size (~1.1s per 25 domains). */
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

export function formatSyncOutcome(o: SyncOutcome): string {
  if (o.status === 'completed') {
    return `Synced ${o.domains_synced} domains — ${o.domains_added} added, ${o.domains_updated} updated.`;
  }
  if (o.status === 'failed') return `Sync failed: ${o.error || 'Unknown error'}`;
  if (o.status === 'stalled') return 'Sync stopped unexpectedly (the server may have restarted mid-sync).';
  return 'Sync is still running — it will finish in the background. Refresh to see the result.';
}

export async function runSync(
  fetchApi: FetchApi,
  accountId: string,
  onProgress?: (elapsedSeconds: number) => void
): Promise<SyncOutcome> {
  const start = await fetchApi<ApiResponse<{ sync_log_id: string; already_running?: boolean }>>(
    `/accounts/${accountId}/sync`,
    { method: 'POST' }
  );

  const logId = start.data?.sync_log_id;
  const alreadyRunning = !!start.data?.already_running;
  if (!logId) throw new Error('Sync did not return a sync log to track');

  const began = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    onProgress?.(Math.round((Date.now() - began) / 1000));

    if (Date.now() - began > POLL_TIMEOUT_MS) {
      return { status: 'timeout', domains_synced: 0, domains_added: 0, domains_updated: 0, alreadyRunning };
    }

    let log: (SyncLog & { stalled?: boolean }) | undefined;
    try {
      const res = await fetchApi<ApiResponse<SyncLog & { stalled?: boolean }>>(`/accounts/sync-logs/${logId}`);
      log = res.data;
    } catch {
      // A transient poll failure shouldn't abort a sync that is still running.
      continue;
    }
    if (!log) continue;

    if (log.status === 'completed') {
      return {
        status: 'completed',
        domains_synced: log.domains_synced ?? 0,
        domains_added: log.domains_added ?? 0,
        domains_updated: log.domains_updated ?? 0,
        alreadyRunning,
      };
    }
    if (log.status === 'failed') {
      return {
        status: 'failed',
        domains_synced: 0,
        domains_added: 0,
        domains_updated: 0,
        error: log.error_message || undefined,
        alreadyRunning,
      };
    }
    if (log.stalled) {
      return { status: 'stalled', domains_synced: 0, domains_added: 0, domains_updated: 0, alreadyRunning };
    }
  }
}
